import { AfterViewInit, Component, DestroyRef, ElementRef, OnInit, ViewChild } from '@angular/core';
import { DatePipe, NgFor, NgIf } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { Offcanvas } from 'bootstrap';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { USER_PERMISSIONS } from '../../app.constants';
import { ACTIVITY_TYPES, ActivityLog, ActivityLogTotals, relatedTypeLabel, toDateInput } from '../task.model';
import { LazyPicker, leadPicker, propertyPicker, staffPicker } from '../lazy-picker';

type LinkType = 'none' | 'property' | 'lead';

@Component({
  selector: 'app-activity-report',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    DatePipe,
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    NgSelectModule,
    BreadcrumbComponent,
    PaginationComponent,
  ],
  templateUrl: './activity-report.component.html',
  styleUrls: ['./activity-report.component.css'],
})
export class ActivityReportComponent implements OnInit, AfterViewInit {
  @ViewChild('activityModal') activityModalElement!: ElementRef;
  private modal: Offcanvas | null = null;

  breadcrumbList = {
    breadcrumb_path: 'Tasks',
    currentURL: 'Daily activity',
    breadcrumb_url: '/admin/tasks',
  };

  logs: ActivityLog[] = [];
  totals: ActivityLogTotals | null = null;
  total = 0;
  page = 1;
  limit = 50;
  totalPage = 1;

  filterForm!: FormGroup;
  activityForm!: FormGroup;
  showModal = false;
  savedCount = 0;
  linkType: LinkType = 'none';
  activityTypes = ACTIVITY_TYPES.map((value) => ({ value, label: value }));

  people: LazyPicker;
  properties: LazyPicker;
  leads: LazyPicker;

  canReports = false;
  canLogActivity = false;
  relatedTypeLabel = relatedTypeLabel;

  constructor(
    private fb: FormBuilder,
    private httpService: HttpServiceService,
    public loadingService: LoadingService,
    private localStorageService: LocalStorageService,
    private alertService: NgxToastrService,
    private destroyRef: DestroyRef,
  ) {
    this.people = staffPicker(httpService, destroyRef, [{ value: 'me', label: 'Me' }]);
    this.properties = propertyPicker(httpService, destroyRef);
    this.leads = leadPicker(httpService, destroyRef);
    this.initForms();
  }

  ngOnInit(): void {
    this.canReports = this.localStorageService.checkPermission(USER_PERMISSIONS.TASKS.TASKS_REPORTS);
    this.canLogActivity = this.localStorageService.checkPermission(USER_PERMISSIONS.TASKS.TASKS_LOG_ACTIVITY);
    this.people.init();
    this.load();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const el = this.activityModalElement?.nativeElement;
      if (el) this.modal = Offcanvas.getOrCreateInstance(el, { backdrop: 'static', keyboard: false });
    });
  }

  private initForms(): void {
    // Default range: this week (Monday → today).
    const today = new Date();
    const monday = new Date(today);
    const day = today.getDay(); // 0 = Sunday
    monday.setDate(today.getDate() - ((day + 6) % 7));
    this.filterForm = this.fb.group({
      user_id: ['me'],
      from: [toDateInput(monday)],
      to: [toDateInput(today)],
    });

    this.activityForm = this.fb.group({
      activity_type: [null, Validators.required],
      date: [toDateInput(today), Validators.required],
      from_time: ['', Validators.required],
      to_time: [''],
      location_text: [''],
      property_id: [null],
      related_id: [null],
      remarks: [''],
    });
  }

  private query(): string {
    const f = this.filterForm.value;
    let q = '';
    if (f.user_id) q += `&user_id=${f.user_id}`;
    if (f.from) q += `&from=${f.from}`;
    if (f.to) q += `&to=${f.to}`;
    return q;
  }

  load(keepPage = false): void {
    if (!keepPage) this.page = 1;
    this.loadingService.show();
    this.httpService.getActivityLogs(this.page, this.limit, this.query()).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.logs = response.data.logs ?? [];
          this.total = response.data.total ?? 0;
          this.totalPage = response.data.pageCount ?? 1;
          this.totals = response.data.totals ?? null;
        }
      },
      error: () => this.loadingService.hide(),
    });
  }

  pageChange(newPage: number): void {
    if (this.page === newPage) return;
    this.page = newPage;
    this.load(true);
  }

  get hoursLabel(): string {
    const m = this.totals?.minutes ?? 0;
    return `${Math.floor(m / 60)}h ${m % 60}m`;
  }

  linkedTo(log: ActivityLog): string {
    if (log.property?.name) return log.property.name;
    if (log.related?.name) return `${relatedTypeLabel(log.related.type)} · ${log.related.name}`;
    return '';
  }

  exportCsv(): void {
    this.loadingService.show();
    this.httpService.exportActivityLogs(this.query()).subscribe({
      next: (blob: Blob) => {
        this.loadingService.hide();
        const f = this.filterForm.value;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `daily-activity_${f.from || ''}_${f.to || ''}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      },
      error: () => {
        this.loadingService.hide();
        this.alertService.error('Could not export the activity report', 'toast-top-right');
      },
    });
  }

  // ── Add activity ───────────────────────────────────────────────────────
  openModal(): void {
    this.savedCount = 0;
    this.activityForm.reset({ date: toDateInput(new Date()), activity_type: null });
    this.linkType = 'none';
    this.properties.init();
    this.leads.init();
    this.showModal = true;
    this.modal?.show();
  }

  closeModal(): void {
    this.showModal = false;
    this.modal?.hide();
  }

  setLinkType(type: LinkType): void {
    this.linkType = type;
    if (type !== 'property') this.activityForm.patchValue({ property_id: null });
    if (type !== 'lead') this.activityForm.patchValue({ related_id: null });
  }

  isFieldInvalid(name: string): boolean {
    const c = this.activityForm.get(name);
    return !!(c && c.invalid && c.touched);
  }

  private toIso(date: string, time: string): string {
    return new Date(`${date}T${time}:00`).toISOString();
  }

  /** Save the activity; with `addAnother` the form stays open for the next entry of the same day. */
  submitActivity(addAnother: boolean = true): void {
    if (this.activityForm.invalid) {
      Object.values(this.activityForm.controls).forEach((c) => c.markAsTouched());
      return;
    }
    const v = this.activityForm.value;
    const body: Record<string, any> = {
      activity_type: v.activity_type,
      started_at: this.toIso(v.date, v.from_time),
    };
    if (v.to_time) body['ended_at'] = this.toIso(v.date, v.to_time);
    if (v.location_text) body['location_text'] = v.location_text;
    if (v.remarks) body['remarks'] = v.remarks;
    if (this.linkType === 'property' && v.property_id) body['property_id'] = v.property_id;
    if (this.linkType === 'lead' && v.related_id) {
      body['related_type'] = 'lead';
      body['related_id'] = v.related_id;
    }
    this.loadingService.show();
    this.httpService.createActivityLog(body).subscribe({
      next: (response: any) => {
        this.loadingService.hide();
        if (response.success) {
          this.savedCount++;
          this.alertService.success(response.message || 'Activity logged', 'toast-top-right');
          this.load(true);
          if (addAnother) this.prepareNextEntry();
          else this.closeModal();
        }
      },
      error: () => this.loadingService.hide(),
    });
  }

  /** Keep Date and Linked to; clear the per-activity fields for the next entry. */
  private prepareNextEntry(): void {
    this.activityForm.patchValue({
      activity_type: null,
      from_time: '',
      to_time: '',
      location_text: '',
      remarks: '',
    });
    ['activity_type', 'from_time', 'to_time', 'location_text', 'remarks'].forEach((name) =>
      this.activityForm.get(name)?.markAsUntouched()
    );
  }
}
